import {useQuery as useApolloQuery} from '@apollo/client';
import {usePerformanceMonitorTrack} from '@sail/observability';
import {useMemoizedValue} from '@sail/react';
import {useCallback, useContext, useEffect, useMemo, useRef} from 'react';
import useGetApolloContext from 'src/internal/apollo/useGetApolloContext';
import {
  DEFAULT_IGNORE_TRACKING_FIELDS,
  DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
  useTrackGraphQlFieldAccesses,
} from 'src/internal/deadwood/deadwood';
import PrefetchEnvContext from 'src/internal/prefetching/PrefetchEnvContext';
import {decrementPrefetchCounter} from 'src/internal/prefetching/decrementPrefetchCounter';
import ensureCorrectFetchPolicy from 'src/internal/prefetching/ensureCorrectFetchPolicy';
import ensureCorrectNextFetchPolicy from 'src/internal/prefetching/ensureCorrectNextFetchPolicy';
import useDefaultFetchPolicy from 'src/internal/prefetching/useDefaultFetchPolicy';
import usePrefetchCounter from 'src/internal/prefetching/usePrefetchCounter';
import {getOperationName} from 'src/internal/shared/utils/gql';
import useDataConfig from 'src/public/useDataConfig';

import type {OperationVariables, WatchQueryFetchPolicy} from '@apollo/client';
import type {
  UseQueryOptions,
  UseQueryOptionsRest,
  UseQueryReturn,
} from 'src/internal/apollo/types';
import type {GraphQlDocument} from 'src/internal/shared/graphql/types/document';

const EMPTY_OPTIONS: Partial<UseQueryOptions<GraphQlDocument<any, any, any>>> =
  {};

/**
 * @see https://sail.stripe.me/apis/data/useQuery
 */
export default function useQuery<
  /** The data returned by the GraphQL query (inferred from the passed GraphQL document). */
  TData,
  /** The variables that the GraphQL query accept (inferred from the passed GraphQL document). */
  TVariables extends OperationVariables,
  /** The permissions needed by the GraphQL query (inferred from the passed GraphQL document). */
  TPermissions,
  TokenIsOptional extends boolean = false,
>(
  /**
   * The GraphQL query that is going to be executed. This must be created with the
   * [`gql`](/apis/data/gql) or
   * [`graphql`](/apis/data/graphql) template literal.
   */
  query: GraphQlDocument<TData, TVariables, TPermissions>,
  /**
   * The options to pass to the query. This argument is optional in the case the query does not
   * have any required variables nor [permissions](/apis/data/permissions).
   * Refer to [operation options](/data/guides/graphql-usage/the-basics-runtime#operation-options) docs.
   */
  ...optionsRest: NoInfer<
    UseQueryOptionsRest<
      GraphQlDocument<TData, TVariables, TPermissions>,
      TokenIsOptional
    >
  >
): /**
 * Object with `data`, `loading` and `error` (similar to the one that the
 * [`useQuery` Apollo hook](https://www.apollographql.com/docs/react/api/react/hooks/#result) returns).
 */
UseQueryReturn<GraphQlDocument<TData, TVariables, TPermissions>> {
  const {isPrefetchMode} = useContext(PrefetchEnvContext);

  const options = optionsRest[0] || EMPTY_OPTIONS;
  const performanceMonitorTrack = usePerformanceMonitorTrack();
  const defaultFetchPolicy = useDefaultFetchPolicy();
  const dataConfig = useDataConfig();

  const operationName = useMemo(() => {
    return getOperationName(query) || '';
  }, [query]);

  let correctFetchPolicy: WatchQueryFetchPolicy | undefined =
    options.fetchPolicy || defaultFetchPolicy;
  let correctNextFetchPolicy: WatchQueryFetchPolicy | undefined =
    options.nextFetchPolicy;

  if (isPrefetchMode) {
    correctFetchPolicy = ensureCorrectFetchPolicy(correctFetchPolicy);
  }

  correctNextFetchPolicy = ensureCorrectNextFetchPolicy(
    correctFetchPolicy,
    correctNextFetchPolicy,
  );

  const optionsWithCorrectFetchPolicy = useMemo(() => {
    const updatedOptions = {
      ...options,
      fetchPolicy: correctFetchPolicy,
    };

    if (correctNextFetchPolicy) {
      updatedOptions.nextFetchPolicy = correctNextFetchPolicy;
    }

    return updatedOptions;
  }, [options, correctFetchPolicy, correctNextFetchPolicy]);

  const operationContext = useGetApolloContext(optionsWithCorrectFetchPolicy);

  const queryOptions = useMemo(() => {
    return {
      ...optionsWithCorrectFetchPolicy,
      context: operationContext,
    };
  }, [operationContext, optionsWithCorrectFetchPolicy]);

  const memoizedVariables = useMemoizedValue(queryOptions.variables);

  usePrefetchCounter({
    operationContext,
    query,
    variables: queryOptions.variables,
    skip: queryOptions.skip || false,
  });

  const execResult = useApolloQuery(query, queryOptions);

  const trackRef = useRef<ReturnType<typeof performanceMonitorTrack> | null>(
    null,
  );

  const startTrack = useCallback(() => {
    if (!trackRef.current) {
      trackRef.current = performanceMonitorTrack(`GraphQL: ${operationName}`);
    }
  }, [operationName, performanceMonitorTrack]);

  const stopTrack = useCallback(() => {
    if (trackRef.current) {
      trackRef.current();
      trackRef.current = null;
    }
  }, []);

  if (!isPrefetchMode) {
    if (execResult.loading) {
      // eslint-disable-next-line react-hooks/refs
      startTrack();
    } else {
      // eslint-disable-next-line react-hooks/refs
      stopTrack();
    }
  }

  useEffect(() => {
    return stopTrack;
  }, [stopTrack]);

  const {
    client,
    refetch: originalRefetch,
    ...data
  } = useTrackGraphQlFieldAccesses(execResult, {
    query,
    sampleRate: options.trackAccesses ?? DEFAULT_TRACK_ACCESSES_SAMPLE_RATE,
    ignoreFields: DEFAULT_IGNORE_TRACKING_FIELDS,
  });

  const refetchWithDecrementPrefetchCounter = useCallback(
    (variables?: Partial<TVariables> | undefined) => {
      const mergedVariables =
        typeof variables !== 'undefined'
          ? {...memoizedVariables, ...variables}
          : memoizedVariables;

      decrementPrefetchCounter({
        service: operationContext.service,
        component: operationContext.component,
        project: operationContext.queryProject,
        operationName,
        variables: mergedVariables,
      });

      return originalRefetch(variables);
    },
    [
      originalRefetch,
      operationName,
      memoizedVariables,
      operationContext.service,
      operationContext.component,
      operationContext.queryProject,
    ],
  );

  const shouldDecrementPrefetchCounterWhenRefetching =
    dataConfig.graphql?.prefetch?.enableMismatchMetrics;

  return shouldDecrementPrefetchCounterWhenRefetching
    ? {refetch: refetchWithDecrementPrefetchCounter, ...data}
    : {refetch: originalRefetch, ...data};
}
